class Collection {
  constructor() {
    this.selectors = {
      section: ".facest-filters-section",
      toolbar: ".m-collection-toolbar",
      cols: [".m-toolbar--column-switcher button"],
      productContainer: "[data-product-container]",
      loadMoreBtn: "[data-load-more]",
      // Sidebar filters selectors
      sidebar: ".m-sidebar",
      sidebarContent: ".m-sidebar--content",
      openSidebar: ".m-sidebar--open",
      closeSidebar: ".m-sidebar--close",
      filtersWrapper: ".m-filter--wrapper",
    };
    this.mobileSortingSelectors = {
      openSorting: ".m-sortby--open",
      closeSorting: ".m-sortby-mobile--close",
      sortingWrapper: ".m-sortby-mobile--wrapper",
      sortingList: ".m-sortby-mobile--content",
    };
    this.sectionId = "";
    this.isDesignMode = MinimogSettings.design_mode;

    this.currentPage = 1;
    this.paginationType; // "paginate" | "loadmore" | "infinite"
    this.totalPages;
    this.totalProducts;
    this.infiniteLoadingObserver;

    this.showColSwitchers;
    this.cachedCol;
    this.initialCol;
    this.activeCol;
    this.STORAGE_KEY = "gridColumnViews";
    this.MAX_COL_BY_SCREEN_SIZE = {
      mobile: 2,
      tablet: 4,
      desktop: 5,
    };

    this.enableFilters;
    this.enableSorting;
    this.filtersType; // "storefront_filters" | "tags_filter"
    this.filtersPosition; // "leftColumn" | "rightColumn" | "fixed"
    this.accordions;
    this.sideEffectEventsAdded = false;
  }

  init = () => {
    // init() runs again after every filter change, so global listeners are added only once
    const isFirstInit = !this.sideEffectEventsAdded;
    this.domNodes = queryDomNodes(this.selectors);
    this.mbSortingNodes = queryDomNodes(this.mobileSortingSelectors);
    this.setData();
    this.initGridView();
    this.initFilters();
    this.initMobileSorting();
    this.initProducts();
    this.sideEffectEventsAdded = true;

    // Handle sticky toolbar on mobile
    this.handleStickyToolbar();
    if (isFirstInit) {
      MinimogEvents.subscribe("ON_PRODUCT_LIST_UPDATED", () => refreshProductReview());
      document.addEventListener("matchMobile", () => this.handleStickyToolbar());
      document.addEventListener("unmatchMobile", () => this.handleStickyToolbar());
    }
  };

  setData = () => {
    const { section, productContainer } = this.domNodes;
    // Grid view data
    this.sectionId = section.dataset.sectionId;
    this.paginationType = section.dataset.paginationType;
    this.totalPages = productContainer && Number(productContainer.dataset.totalPages);
    this.view = section.dataset.view;
    this.activeCol = null;
    // The page the server rendered (e.g. /collections/x?page=3), so it is not loaded a second time
    this.currentPage = (productContainer && Number(productContainer.dataset.currentPage)) || 1;
    this.loadedPages = new Set([this.currentPage]);
    this.initialCol = Number(section.dataset.initialColumn);
    this.cachedCol = Number(window.localStorage.getItem(this.STORAGE_KEY)) || this.initialCol;
    this.showColSwitchers = section.dataset.showColSwitchers === "true";
    // Filters data
    this.enableFilters = section.dataset.enableFilters === "true";
    this.enableSorting = section.dataset.enableSorting === "true";
    this.filtersType = section.dataset.filtersType;
    this.filtersPosition = section.dataset.filtersPosition;
  };

  initGridView = () => {
    this.toggleView(this.isDesignMode ? this.initialCol : this.cachedCol);
    this.updateViewByScreen();
    addEventDelegate({
      selector: this.selectors.cols[0],
      context: this.domNodes.toolbar,
      handler: (e, colNode) => this.toggleView(Number(colNode.dataset.column)),
    });
    this.initLoadMore();
    if (!this.sideEffectEventsAdded) {
      window.addEventListener("resize", debounce(this.updateViewByScreen, 500));
    }
  };

  initFilters = () => {
    if (!this.enableFilters) return;
    const { sidebar, openSidebar, closeSidebar } = this.domNodes;
    window.requestAnimationFrame(this.initAccordions);
    openSidebar.addEventListener("click", this.openSidebarFilter);
    closeSidebar.addEventListener("click", this.closeSidebarFilter);
    sidebar.addEventListener("click", (e) => e.target === sidebar && this.closeSidebarFilter());
  };

  initMobileSorting = () => {
    if (!this.enableSorting) return;
    const { openSorting, closeSorting, sortingWrapper } = this.mbSortingNodes;
    openSorting.addEventListener("click", this.openMobileSorting);
    closeSorting.addEventListener("click", this.closeMobileSorting);
    sortingWrapper.addEventListener("click", (e) => e.target === sortingWrapper && this.closeMobileSorting());
  };

  openMobileSorting = () => {
    const { sortingWrapper, sortingList } = this.mbSortingNodes;
    sortingWrapper.style.display = "block";
    window.requestAnimationFrame(() => {
      sortingWrapper.style.setProperty("--m-bg-opacity", "0.5");
      sortingList.style.setProperty("--m-translate-y", "0");
    });
  };

  closeMobileSorting = (e) => {
    const { sortingWrapper, sortingList } = this.mbSortingNodes;
    sortingWrapper.style.setProperty("--m-bg-opacity", "0");
    sortingList.style.setProperty("--m-translate-y", "100%");
    setTimeout(() => sortingWrapper.style.setProperty("display", "none"), 300);
  };

  initAccordions = () => {
    this.accordions && this.accordions.destroy();
    const { filtersWrapper } = this.domNodes;
    filtersWrapper.classList.remove("acc-initialized");
    this.accordions = new MinimogLibs.Accordion(filtersWrapper, {
      presetContentHeight: window.innerWidth > 1280 && this.filtersPosition !== "fixed",
      callback: () => filtersWrapper.style.setProperty('opacity', '1')
    });
  };

  openSidebarFilter = () => {
    const { sidebar, sidebarContent, section } = this.domNodes;
    sidebar.style.display = "block";
    window.requestAnimationFrame(() => {
      sidebar.style.setProperty("--m-bg-opacity", "0.5");
      sidebarContent.style.setProperty("--m-translate-x", "0");
      this.accordions && this.accordions.setContentHeight();
    });
    section.classList.add("sidebar-open");
  };

  closeSidebarFilter = () => {
    const { sidebar, sidebarContent, section } = this.domNodes;
    section.classList.remove("sidebar-open");
    if (window.innerWidth < 1280 || sidebar.dataset.type === "fixed") {
      sidebarContent.style.setProperty("--m-translate-x", "-100%");
      sidebar.style.removeProperty("--m-bg-opacity");
      setTimeout(() => sidebar.style.removeProperty("display"), 300);
    }
  };

  toggleView = (col) => {
    if (!col || this.view === "search") return;
    const { cols, productContainer } = this.domNodes;
    if (!productContainer) return;
    // Resize image product card
    const allImages = productContainer.querySelectorAll("responsive-image");
    if (allImages && allImages.length > 0) {
      allImages.forEach((img) => {
        img.disconnectedCallback();
        img.connectedCallback();
      });
    }

    const activeCol = this.activeCol || this.initialCol;
    if (this.showColSwitchers) {
      cols[activeCol - 1].classList.remove("active");
      cols[col - 1].classList.add("active");
    }

    productContainer.classList.remove(`m-cols-${activeCol}`);
    productContainer.classList.add(`m-cols-${col}`);

    window.localStorage.setItem(this.STORAGE_KEY, col);
    this.activeCol = col;
  };

  updateViewByScreen = () => {
    if (this.activeCol === 1) return; // List layout
    const device = this.getDeviceByScreenSize();
    const maxCol = this.MAX_COL_BY_SCREEN_SIZE[device];
    if (maxCol < this.activeCol) this.toggleView(maxCol);
  };

  getDeviceByScreenSize = () => {
    if (window.innerWidth < 768) return "mobile";
    if (window.innerWidth < 1024) return "tablet";
    return "desktop";
  };

  initLoadMore = () => {
    // A new grid (after filtering) replaces the old one: drop its observer and ignore its pending request
    this.infiniteLoadingObserver && this.infiniteLoadingObserver.disconnect();
    this.infiniteLoadingObserver = null;
    this.loadId = (this.loadId || 0) + 1;
    this.isLoading = false;
    this.failedLoads = 0;
    if (this.paginationType === "paginate" || this.totalPages <= 1 || this.view === "search") return;

    const { loadMoreBtn } = this.domNodes;
    loadMoreBtn.addEventListener("click", this.loadMoreProducts);

    if (this.paginationType === "infinite") {
      // Load as soon as the button is on screen. Waiting until it is 100% visible stalled on mobile:
      // at the bottom of the page the button can sit a few px above the viewport and never be fully visible.
      this.infiniteLoadingObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.loadMoreProducts();
        });
      });

      this.infiniteLoadingObserver.observe(loadMoreBtn);
    }
  };

  // Observing again makes the observer report the button's current state, so a button that is
  // still on screen after a load (or a failed load) triggers the next one.
  observeLoadMore = () => {
    const { loadMoreBtn } = this.domNodes;
    if (!this.infiniteLoadingObserver || !loadMoreBtn) return;
    this.infiniteLoadingObserver.unobserve(loadMoreBtn);
    this.infiniteLoadingObserver.observe(loadMoreBtn);
  };

  // Lowest page not shown yet: after landing on ?page=N, pages 1..N-1 are loaded too
  getNextPage = () => {
    for (let page = 1; page <= this.totalPages; page++) {
      if (!this.loadedPages.has(page)) return page;
    }
    return 0;
  };

  loadMoreProducts = () => {
    // One request at a time: the observer and the button keep firing while a page is loading
    if (this.isLoading) return;
    const nextPage = this.getNextPage();
    if (!nextPage) return;

    const { productContainer, loadMoreBtn } = this.domNodes;
    const loadId = this.loadId;
    this.isLoading = true;
    this.toggleLoading(true);
    fetchSection(this.sectionId, { params: { page: nextPage } })
      .then((productGridHTML) => {
        if (loadId !== this.loadId) return;
        const productNodes = productGridHTML.querySelectorAll("[data-product-container] .m-product-item");
        productNodes.forEach((prodNode) => {
          // Skip a product that is already in the grid (e.g. the sort order shifted between pages)
          const card = prodNode.querySelector("[data-product-id]");
          const productId = card && card.dataset.productId;
          if (productId && productContainer.querySelector(`[data-product-id="${productId}"]`)) return;
          productContainer.appendChild(prodNode);
        });
        this.loadedPages.add(nextPage);
        this.currentPage = nextPage;
        this.failedLoads = 0;
        this.initProducts();
      })
      .catch((err) => {
        if (loadId === this.loadId) this.failedLoads++;
        console.error(`Failed to load more products.`, err);
      })
      .finally(() => {
        if (loadId !== this.loadId) return;
        MinimogEvents.emit("ON_PRODUCT_LIST_UPDATED");
        this.toggleLoading(false);
        this.isLoading = false;
        if (!this.getNextPage()) {
          loadMoreBtn.parentNode.remove();
          this.infiniteLoadingObserver && this.infiniteLoadingObserver.unobserve(loadMoreBtn);
          return;
        }
        // A failed page is not skipped: it is requested again (with a delay, at most 3 times automatically)
        if (!this.failedLoads) this.observeLoadMore();
        else if (this.failedLoads < 3) {
          setTimeout(() => loadId === this.loadId && !this.isLoading && this.observeLoadMore(), this.failedLoads * 2000);
        }
      });
  };

  initProducts = () => {
    MinimogTheme.CompareProduct && MinimogTheme.CompareProduct.setCompareButtonsState();
    MinimogTheme.Wishlist && MinimogTheme.Wishlist.setWishlistButtonsState();
  };

  toggleLoading = (loading) => {
    const { loadMoreBtn } = this.domNodes;
    if (loadMoreBtn) {
      const method = loading ? "add" : "remove";
      loadMoreBtn.classList[method]("m-spinner-loading");
    }
  };

  handleStickyToolbar() {
    const toolbar = this.domNodes.toolbar;
    window.removeEventListener("scroll", this.onStickyToolbarScroll);
    if (!toolbar) return;
    let lastScroll = 0;
    this.onStickyToolbarScroll = () => {
      if (MinimogTheme.config.mqlMobile) {
        const currentScroll = window.scrollY;
        // The toolbar is position: sticky on mobile, so once it is stuck its offsetTop follows the scroll
        // position. Measure where it sits in the page from its parent (the toolbar is its first child).
        const extraSpace = toolbar.parentElement.getBoundingClientRect().top + currentScroll;
        if (currentScroll <= extraSpace) {
          toolbar.classList.remove("scroll-up", "m-collection-toolbar--sticky");
          return;
        }
        toolbar.classList.add("m-collection-toolbar--sticky");
        if (
          currentScroll > toolbar.offsetHeight + extraSpace &&
          currentScroll > lastScroll &&
          !toolbar.classList.contains("scroll-down")
        ) {
          toolbar.classList.remove("scroll-up");
          toolbar.classList.add("scroll-down");
        } else if (currentScroll < lastScroll && toolbar.classList.contains("scroll-down")) {
          toolbar.classList.remove("scroll-down");
          toolbar.classList.add("scroll-up");
        }
        lastScroll = currentScroll;
      } else {
        toolbar.classList.remove("scroll-up", "m-collection-toolbar--sticky", "scroll-down");
      }
    };
    window.addEventListener("scroll", this.onStickyToolbarScroll);
  }
}